/**
 * API Service - Frontend calls to Workers Backend
 * Connects frontend to Edge Commerce Protocol Engine API
 * Version: 2.0.1
 */

// API Base URL - Configure based on environment
// Auto-rotating reversal proxy with fallback
const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8787";
const FALLBACK_MODES = ["mock", "local"];

// Type definitions for API responses
export interface DashboardMetrics {
  backendLatency: Record<string, number>;
  requestStats: {
    totalRequests: number;
    successfulRequests: number;
    failedRequests: number;
    errorRate: number;
    avgResponseTime: number;
  };
  transactionMetrics: {
    totalVolume: number;
    completedTransactions: number;
    pendingTransactions: number;
    failedTransactions: number;
  };
}

export interface RevolutionaryMetrics {
  predictive: {
    totalUsers: number;
    totalPreloads: number;
    averageAccuracy: number;
    quantumStates: number;
  };
  evolution: {
    totalAgents: number;
    currentGeneration: number;
    averageFitness: number;
    totalMutations: number;
  };
  quantum: {
    totalAgents: number;
    coherenceLevel: number;
    entanglementDegree: number;
    averageQuantumSpeedup: number;
  };
  temporal: {
    totalStates: number;
    totalBranches: number;
    averageStatesPerBranch: number;
  };
  preExecution: {
    totalPreExecutions: number;
    cacheHitRate: number;
    predictionAccuracy: number;
  };
  hyperDimensional: {
    totalContexts: number;
    averageDimensions: number;
    foldedContexts: number;
  };
  overall: {
    totalInnovations: number;
    systemHealth: number;
    revolutionScore: number;
  };
}

class ApiService {
  private baseUrl: string;
  private headers: HeadersInit;
  private currentFallbackIndex: number = 0;
  private backendAvailable: boolean = true;
  private lastBackendCheck: number = 0;
  private healthCheckInterval: number = 5000; // 5 seconds

  constructor(baseUrl: string = API_BASE) {
    console.log("=== API Service Initialized with Auto-Rotating Proxy ===");
    this.baseUrl = baseUrl;
    this.headers = {
      "Content-Type": "application/json",
    };
    console.log("Primary Backend:", this.baseUrl);
    this.checkBackendHealth();
  }

  // Check backend health with automatic fallback
  private async checkBackendHealth(): Promise<boolean> {
    const now = Date.now();
    if (now - this.lastBackendCheck < this.healthCheckInterval) {
      return this.backendAvailable;
    }

    this.lastBackendCheck = now;

    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: "GET",
        headers: this.headers,
        signal: AbortSignal.timeout(5000), // 5 second timeout
      });

      if (response.ok) {
        this.backendAvailable = true;
        console.log("✓ Backend health check passed");
        return true;
      }
    } catch (error) {
      console.warn("✗ Backend health check failed, using fallback");
      this.backendAvailable = false;
      return false;
    }

    this.backendAvailable = false;
    return false;
  }

  // Get current active endpoint (primary or fallback)
  private getActiveEndpoint(): string {
    if (this.backendAvailable) {
      return this.baseUrl;
    }
    return FALLBACK_MODES[this.currentFallbackIndex];
  }

  // Rotate to next fallback mode
  private rotateFallback(): void {
    this.currentFallbackIndex =
      (this.currentFallbackIndex + 1) % FALLBACK_MODES.length;
    console.log(
      `Rotating to fallback mode: ${FALLBACK_MODES[this.currentFallbackIndex]}`,
    );
  }

  // Set authorization token
  setToken(token: string) {
    this.headers = { ...this.headers, Authorization: `Bearer ${token}` };
  }

  // Set tenant ID
  setTenantId(tenantId: string) {
    this.headers = { ...this.headers, "X-Tenant-ID": tenantId };
  }

  // Generic request handler with auto-rotating proxy
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    console.log("=== API Request Method Called ===");
    console.log("Endpoint:", endpoint);
    console.log("Options:", options);

    // Check backend health
    await this.checkBackendHealth();

    const activeEndpoint = this.getActiveEndpoint();
    console.log("Active Endpoint:", activeEndpoint);

    // Fallback mode - return mock data
    if (activeEndpoint === "mock") {
      console.log("Using mock mode for:", endpoint);
      await new Promise((resolve) => setTimeout(resolve, 300)); // Simulate network delay

      const mockResponses: Record<string, any> = {
        "/dashboard/metrics": {
          backendLatency: {
            payment: 85,
            ecommerce: 120,
            inventory: 45,
            tenants: 78,
            analytics: 92,
            reports: 65,
          },
          requestStats: {
            totalRequests: 12345,
            successfulRequests: 12123,
            failedRequests: 222,
            errorRate: 1.8,
            avgResponseTime: 95,
          },
          transactionMetrics: {
            totalVolume: 1234567,
            completedTransactions: 4567,
            pendingTransactions: 234,
            failedTransactions: 45,
          },
        },
        "/dashboard/stats": {
          totalOrders: 567,
          pendingOrders: 23,
          completedOrders: 544,
          revenue: 12345,
        },
        "/api/payments/create": {
          id: "pay_" + Date.now(),
          status: "created",
        },
        "/api/payments/verify": {
          verified: true,
        },
        "/api/ecommerce/products": [
          { id: 1, name: "Product A", price: 29.99, stock: 100 },
          { id: 2, name: "Product B", price: 49.99, stock: 50 },
          { id: 3, name: "Product C", price: 19.99, stock: 200 },
        ],
        "/api/tenant/info": [
          {
            id: 1,
            name: "Tenant A",
            domain: "tenant-a.example.com",
            active: true,
          },
          {
            id: 2,
            name: "Tenant B",
            domain: "tenant-b.example.com",
            active: false,
          },
        ],
        "/revolutionary/metrics": {
          predictive: {
            totalUsers: 42,
            totalPreloads: 156,
            averageAccuracy: 0.87,
            quantumStates: 128,
          },
          evolution: {
            totalAgents: 24,
            currentGeneration: 7,
            averageFitness: 0.82,
            totalMutations: 89,
          },
          quantum: {
            totalAgents: 24,
            coherenceLevel: 0.94,
            entanglementDegree: 0.67,
            averageQuantumSpeedup: 2.3,
          },
          temporal: {
            totalStates: 1247,
            totalBranches: 8,
            averageStatesPerBranch: 156,
          },
          preExecution: {
            totalPreExecutions: 312,
            cacheHitRate: 0.73,
            predictionAccuracy: 0.81,
          },
          hyperDimensional: {
            totalContexts: 89,
            averageDimensions: 128,
            foldedContexts: 23,
          },
          overall: {
            totalInnovations: 1856,
            systemHealth: 0.91,
            revolutionScore: 0.94,
          },
        },
      };

      const mockResponse = mockResponses[endpoint] || [];
      console.log("Mock API Response:", mockResponse);
      return mockResponse;
    }

    // Real HTTP request
    const url = `${this.baseUrl}${endpoint}`;
    console.log("Full URL:", url);

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.headers,
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `HTTP error! status: ${response.status}, body: ${errorText}`,
        );
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("API Response:", data);

      // Handle the backend response format which includes success wrapper
      if (data && typeof data === "object" && "success" in data) {
        if (data.success) {
          return data.data || data; // Return data field if exists, otherwise full response
        } else {
          throw new Error(data.error || "API request failed");
        }
      }

      return data;
    } catch (error) {
      console.error("API Request Error:", error);
      console.log("Rotating to fallback...");
      this.rotateFallback();
      // Retry with fallback
      return this.request<T>(endpoint, options);
    }
  }

  // ============ Authentication Endpoints ============
  async login(email: string, password: string) {
    return this.request("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  }

  async verifyToken(token: string) {
    return this.request("/api/auth/verify", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  // ============ Payment Endpoints ============
  async createPayment(data: {
    amount: number;
    currency: string;
    orderId: string;
  }) {
    return this.request("/api/payments/create", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async verifyPayment(paymentId: string) {
    return this.request(`/api/payments/verify?id=${paymentId}`);
  }

  async refundPayment(paymentId: string) {
    return this.request("/api/payments/refund", {
      method: "POST",
      body: JSON.stringify({ paymentId }),
    });
  }

  // ============ E-commerce Endpoints ============
  async getProducts(params?: {
    category?: string;
    limit?: number;
    offset?: number;
  }) {
    const query = new URLSearchParams(params as any).toString();
    return this.request(`/api/ecommerce/products?${query}`);
  }

  async createProduct(data: any) {
    return this.request("/api/ecommerce/products", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // ============ Tenant Management ============
  async getTenants() {
    return this.request("/api/tenant/info");
  }

  async registerTenant(data: any) {
    return this.request("/api/tenant/register", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // ============ Dashboard Endpoints ============
  async getDashboardMetrics(): Promise<DashboardMetrics> {
    return this.request<DashboardMetrics>("/dashboard/metrics");
  }

  async getDashboardStats() {
    return this.request("/dashboard/stats");
  }

  async getDashboardHealth() {
    return this.request("/dashboard/health");
  }

  // ============ Revolutionary Dashboard Endpoints ============
  async getRevolutionaryMetrics(): Promise<RevolutionaryMetrics> {
    return this.request<RevolutionaryMetrics>("/revolutionary/metrics");
  }
}

// Create and export singleton instance
export const api = new ApiService();
